// Copyright 2025, University of Maryland, All Rights Reserved

type CoverageLevel = 'H' | 'M' | 'L' | null
type CombinedLevel = 'H' | 'M' | 'L' | 'None'

interface SupportingClo {
  course_label: string
  prerequisite_clo_id: string
  prerequisite_clo_statement?: string
  support_relationship: 'Direct' | 'Foundational' | 'Partial' | 'Weak' | 'None'
  prerequisite_coverage_level: CoverageLevel
}

interface ReadinessItem {
  requiring_clo_id: string
  requiring_clo_statement: string
  requiring_coverage_level: CoverageLevel
  supporting_prerequisite_clos: SupportingClo[]
  combined_support_level: CombinedLevel
  cognitive_alignment: 'Aligned' | 'Gap'
  gap_severity: 'Critical' | 'Moderate' | 'Minor' | 'None'
  missing_concepts: string[]
  gap_explanation: string
}

export interface PrerequisiteReadinessProps {
  requiring_clo_readiness: ReadinessItem[]
  readiness_rating: 'Adequate' | 'Partial' | 'Inadequate'
  readiness_explanation: string
  summary?: string
  prerequisite_labels?: string[]
  requiring_label?: string
}

const LEVEL_LABEL: Record<string, string> = { H: 'High', M: 'Medium', L: 'Low' }

const SEVERITY_CONFIG = {
  Critical: { badge: 'bg-red-100 text-red-800 border border-red-200', dot: 'bg-red-500' },
  Moderate: { badge: 'bg-orange-100 text-orange-800 border border-orange-200', dot: 'bg-orange-400' },
  Minor:    { badge: 'bg-yellow-100 text-yellow-800 border border-yellow-200', dot: 'bg-yellow-400' },
  None:     { badge: 'bg-green-100 text-green-800 border border-green-200', dot: 'bg-green-500' },
}

const RELATIONSHIP_CONFIG: Record<string, string> = {
  Direct:       'bg-emerald-100 text-emerald-800 border border-emerald-200',
  Foundational: 'bg-teal-100 text-teal-800 border border-teal-200',
  Partial:      'bg-amber-100 text-amber-800 border border-amber-200',
  Weak:         'bg-gray-100 text-gray-600 border border-gray-200',
  None:         'bg-gray-100 text-gray-500 border border-gray-200',
}

const RELATIONSHIP_LEGEND: { key: string; desc: string }[] = [
  { key: 'Direct',       desc: 'teaches essentially the same skill' },
  { key: 'Foundational', desc: 'teaches an enabling building-block' },
  { key: 'Partial',      desc: 'covers only part of what is needed' },
  { key: 'Weak',         desc: 'only a tangential connection' },
]

function CoverageChip({ level }: { level: CoverageLevel }) {
  if (!level) return <span className="text-xs text-gray-400 italic">Not present</span>
  const colors: Record<string, string> = {
    H: 'bg-[#E21833] text-white',
    M: 'bg-[#FFD520] text-gray-800',
    L: 'bg-[#D3D3D3] text-gray-700',
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${colors[level]}`}>
      {level} — {LEVEL_LABEL[level]}
    </span>
  )
}

function SupportChip({ level }: { level: CombinedLevel }) {
  if (level === 'None') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-500 border border-gray-200">No support</span>
  }
  return <CoverageChip level={level} />
}

export function PrerequisiteReadiness({
  requiring_clo_readiness,
  readiness_rating,
  readiness_explanation,
  summary,
  prerequisite_labels = [],
  requiring_label = 'Requiring Course',
}: PrerequisiteReadinessProps) {
  const items = requiring_clo_readiness || []
  const criticalCount = items.filter(g => g.gap_severity === 'Critical').length
  const moderateCount = items.filter(g => g.gap_severity === 'Moderate').length
  const minorCount = items.filter(g => g.gap_severity === 'Minor').length
  const adequateCount = items.filter(g => g.gap_severity === 'None').length
  const cognitiveGaps = items.filter(g => g.cognitive_alignment === 'Gap').length

  return (
    <div className="space-y-6 prereq-readiness-report">

      {/* Print styling: render WYSIWYG (like the analysis-report print) — the base
          print stylesheet already forces pill colors to print. The table is wider
          than a portrait page, so we make it FIT the page (fixed layout + wrapping)
          instead of letting the overflow-x container clip the right-hand columns. */}
      <style>{`
        @media print {
          .prereq-readiness-report { width: 100% !important; }
          .prereq-readiness-report .shadow-lg,
          .prereq-readiness-report .shadow-sm { box-shadow: none !important; }

          /* Stop the horizontal-scroll container from clipping columns on paper */
          .prereq-readiness-report .overflow-x-auto { overflow: visible !important; }

          /* Force the table to fit the page width and wrap cell text */
          .prereq-readiness-report table {
            table-layout: fixed !important;
            width: 100% !important;
            font-size: 8.5pt !important;
          }
          .prereq-readiness-report th,
          .prereq-readiness-report td {
            white-space: normal !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
            padding: 4px 6px !important;
            vertical-align: top !important;
          }
          /* Column widths tuned for the 7 columns (sum = 100%) */
          .prereq-readiness-report th:nth-child(1), .prereq-readiness-report td:nth-child(1) { width: 4% !important; }
          .prereq-readiness-report th:nth-child(2), .prereq-readiness-report td:nth-child(2) { width: 23% !important; }
          .prereq-readiness-report th:nth-child(3), .prereq-readiness-report td:nth-child(3) { width: 9% !important; }
          .prereq-readiness-report th:nth-child(4), .prereq-readiness-report td:nth-child(4) { width: 27% !important; }
          .prereq-readiness-report th:nth-child(5), .prereq-readiness-report td:nth-child(5) { width: 9% !important; }
          .prereq-readiness-report th:nth-child(6), .prereq-readiness-report td:nth-child(6) { width: 8% !important; }
          .prereq-readiness-report th:nth-child(7), .prereq-readiness-report td:nth-child(7) { width: 20% !important; }

          .prereq-readiness-report tr { break-inside: avoid; page-break-inside: avoid; }
          .prereq-readiness-report .bg-white { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>

      {/* Readiness rating card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-2">
          <h3 className="text-xl font-bold text-gray-900">Prerequisite Readiness</h3>
          <span className="px-3 py-1 rounded-full text-sm font-semibold bg-gray-100 text-gray-700 border border-gray-200">
            {readiness_rating}
          </span>
        </div>

        {/* Course context: prerequisites → requiring */}
        <div className="text-sm text-gray-700 mb-3 flex flex-wrap items-center gap-1.5">
          {prerequisite_labels.length > 0 && (
            <>
              {prerequisite_labels.map((lbl, i) => (
                <span key={i} className="inline-flex items-center px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-100 text-xs font-medium">
                  {lbl}
                </span>
              ))}
              <span className="mx-1 text-gray-400">→</span>
            </>
          )}
          <span className="inline-flex items-center px-2 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-100 text-xs font-semibold">
            {requiring_label}
          </span>
        </div>

        <p className="text-sm text-gray-600 mb-3">{readiness_explanation}</p>

        {/* Gap summary chips */}
        <div className="flex flex-wrap gap-2 text-xs">
          {criticalCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200 font-medium">
              {criticalCount} Critical
            </span>
          )}
          {moderateCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-orange-50 text-orange-700 border border-orange-200 font-medium">
              {moderateCount} Moderate
            </span>
          )}
          {minorCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-50 text-yellow-700 border border-yellow-200 font-medium">
              {minorCount} Minor
            </span>
          )}
          {adequateCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-50 text-green-700 border border-green-200 font-medium">
              {adequateCount} Adequate
            </span>
          )}
          {cognitiveGaps > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-200 font-medium">
              {cognitiveGaps} cognitive gap{cognitiveGaps !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {summary && <p className="text-sm text-gray-600 mt-4 pt-4 border-t border-gray-100">{summary}</p>}
      </div>

      {/* Readiness by CLO table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-800">Readiness by CLO</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            For each objective in <span className="font-medium text-gray-700">{requiring_label}</span>,
            how well the prerequisite course{prerequisite_labels.length > 1 ? 's' : ''} prepare students.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">#</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requiring CLO</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Demand</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Prereq support</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Cognitive</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gap</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Explanation</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map((item, idx) => {
                const sev = SEVERITY_CONFIG[item.gap_severity] ?? SEVERITY_CONFIG.None
                const supports = item.supporting_prerequisite_clos || []
                return (
                  <tr key={item.requiring_clo_id} className="hover:bg-gray-50 align-top">
                    <td className="px-4 py-3 text-sm font-medium text-gray-500">{idx + 1}</td>

                    <td className="px-4 py-3 text-sm text-gray-900 w-1/4">
                      <p title={item.requiring_clo_statement}>{item.requiring_clo_statement}</p>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      <CoverageChip level={item.requiring_coverage_level} />
                    </td>

                    {/* Prereq support: combined level + the supporting prerequisite CLOs */}
                    <td className="px-4 py-3 w-1/4">
                      <SupportChip level={item.combined_support_level} />
                      {supports.length > 0 ? (
                        <ul className="mt-2 space-y-2">
                          {supports.map((s, i) => (
                            <li key={i} className="text-xs">
                              <p className="text-gray-700">{s.prerequisite_clo_statement || s.prerequisite_clo_id}</p>
                              <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-gray-500">
                                <span
                                  className="inline-flex items-center px-1.5 py-0.5 rounded font-medium bg-indigo-50 text-indigo-700 border border-indigo-100 max-w-[160px] truncate"
                                  title={s.course_label}
                                >
                                  {s.course_label}
                                </span>
                                <span className="text-gray-400">·</span>
                                <span>{s.support_relationship}</span>
                                {s.prerequisite_coverage_level && (
                                  <>
                                    <span className="text-gray-400">·</span>
                                    <span className="font-semibold">{s.prerequisite_coverage_level}</span>
                                  </>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="mt-1 text-[11px] text-gray-400 italic">No supporting CLO</p>
                      )}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                        item.cognitive_alignment === 'Aligned'
                          ? 'bg-green-100 text-green-800 border border-green-200'
                          : 'bg-orange-100 text-orange-800 border border-orange-200'
                      }`}>
                        {item.cognitive_alignment}
                      </span>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-semibold ${sev.badge}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${sev.dot}`} />
                        {item.gap_severity}
                      </span>
                    </td>

                    {/* Explanation + missing concepts */}
                    <td className="px-4 py-3 text-sm text-gray-700 w-1/4">
                      <p title={item.gap_explanation}>{item.gap_explanation}</p>
                      {item.missing_concepts && item.missing_concepts.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          <span className="text-[11px] text-gray-400 mr-0.5">Missing:</span>
                          {item.missing_concepts.map((c, i) => (
                            <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] bg-red-50 text-red-700 border border-red-100">
                              {c}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legends */}
      <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Gap Severity Legend</p>
          <div className="flex flex-wrap gap-3">
            {(Object.entries(SEVERITY_CONFIG) as [keyof typeof SEVERITY_CONFIG, typeof SEVERITY_CONFIG[keyof typeof SEVERITY_CONFIG]][]).map(([key, cfg]) => (
              <span key={key} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${cfg.badge}`}>
                <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                {key}
                {key === 'Critical' && ' — required but not prepared'}
                {key === 'Moderate' && ' — partially prepared'}
                {key === 'Minor' && ' — small gap'}
                {key === 'None' && ' — adequately prepared'}
              </span>
            ))}
          </div>
        </div>

        <div className="pt-4 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Support Relationship Legend</p>
          <div className="flex flex-wrap gap-3">
            {RELATIONSHIP_LEGEND.map(({ key, desc }) => (
              <span key={key} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${RELATIONSHIP_CONFIG[key] || RELATIONSHIP_CONFIG.None}`}>
                {key} — {desc}
              </span>
            ))}
          </div>
          <p className="text-[11px] text-gray-400 mt-2">
            Relationship describes <span className="font-medium">how relevant</span> a prerequisite objective is;
            the H/M/L level shows <span className="font-medium">how deeply</span> the prerequisite course taught it.
          </p>
        </div>
      </div>

    </div>
  )
}
